import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/AdminLayout';
import { LoadingState, ConfirmModal } from '../../../components/admin/shared';
import { Save, CheckCircle, Globe, Briefcase, BookOpen, FileText, Award, Plane, Building, Luggage, MapPin, Users as UsersIcon } from 'lucide-react';
import educentialApi from '../../../api/educentialApi';

const FeatureManagement = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const featureDefinitions = [
    {
      id: 'overseas',
      name: 'Overseas Education',
      description: 'Study abroad programs and university applications',
      icon: Globe,
      color: 'blue'
    },
    {
      id: 'career',
      name: 'Career Counseling',
      description: 'Professional career guidance and planning services',
      icon: Briefcase,
      color: 'purple'
    },
    {
      id: 'skills',
      name: 'Skill Development',
      description: 'Training programs and skill enhancement courses',
      icon: BookOpen,
      color: 'green'
    },
    {
      id: 'test-prep',
      name: 'Test Preparation',
      description: 'Preparation for standardized tests (IELTS, TOEFL, GRE, etc.)',
      icon: FileText,
      color: 'yellow'
    },
    {
      id: 'scholarships',
      name: 'Scholarship Finder',
      description: 'Find and apply for scholarships and financial aid',
      icon: Award,
      color: 'teal'
    },
    {
      id: 'visa',
      name: 'Visa Assistance',
      description: 'Help with visa applications and documentation',
      icon: Plane,
      color: 'indigo'
    },
    {
      id: 'accommodation',
      name: 'Accommodation',
      description: 'Student housing and accommodation services',
      icon: Building,
      color: 'pink'
    },
    {
      id: 'pre-departure',
      name: 'Pre-Departure',
      description: 'Pre-departure orientation and preparation',
      icon: Luggage,
      color: 'orange'
    },
    {
      id: 'post-arrival',
      name: 'Post-Arrival',
      description: 'Support services after arrival in destination country',
      icon: MapPin,
      color: 'red'
    },
    {
      id: 'alumni',
      name: 'Alumni Network',
      description: 'Connect with alumni and networking opportunities',
      icon: UsersIcon,
      color: 'cyan'
    }
  ];

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    setLoading(true);
    try {
      const response = await educentialApi.get('/api/admin/settings/features');

      // Merge with feature definitions
      const loadedFeatures = featureDefinitions.map(def => {
        const saved = response.data.find(f => f.id === def.id);
        return {
          ...def,
          enabled: saved?.enabled !== undefined ? saved.enabled : def.id === 'overseas',
          availableFrom: saved?.availableFrom || '',
          availableUntil: saved?.availableUntil || '',
          settings: saved?.settings || {}
        };
      });

      setFeatures(loadedFeatures);
    } catch (error) {
      console.error('Error loading features:', error);
      // Initialize with defaults if API fails
      setFeatures(featureDefinitions.map(def => ({
        ...def,
        enabled: def.id === 'overseas',
        availableFrom: '',
        availableUntil: '',
        settings: {}
      })));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeature = (featureId) => {
    setFeatures(prev => prev.map(f =>
      f.id === featureId ? { ...f, enabled: !f.enabled } : f
    ));
  };

  const handleSave = async () => {
    setShowSaveModal(false);
    setSaving(true);

    try {
      await educentialApi.post('/api/admin/settings/features', {
        features: features.map(f => ({
          id: f.id,
          enabled: f.enabled,
          availableFrom: f.availableFrom,
          availableUntil: f.availableUntil,
          settings: f.settings
        }))
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving features:', error);
      alert('Failed to save feature settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
      green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' },
      yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600', border: 'border-yellow-200' },
      teal: { bg: 'bg-teal-100', text: 'text-teal-600', border: 'border-teal-200' },
      indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200' },
      pink: { bg: 'bg-pink-100', text: 'text-pink-600', border: 'border-pink-200' },
      orange: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-200' },
      red: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200' },
      cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200' }
    };
    return colors[color] || colors.blue;
  };

  if (loading) {
    return (
      <AdminLayout title="Feature Management">
        <LoadingState message="Loading features..." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Feature Management">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Feature Management</h2>
            <p className="text-gray-600 mt-1">Enable or disable platform features for all users</p>
          </div>
          <button
            onClick={() => setShowSaveModal(true)}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-900 font-medium">Feature settings saved successfully!</p>
          </div>
        )}

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((feature) => {
            const Icon = feature.icon;
            const colorClasses = getColorClasses(feature.color);

            return (
              <div
                key={feature.id}
                className={`bg-white rounded-lg shadow border-2 transition-all ${
                  feature.enabled ? `${colorClasses.border}` : 'border-gray-200'
                }`}
              >
                <div className="p-6">
                  {/* Feature Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-3">
                      <div className={`p-3 rounded-lg ${colorClasses.bg}`}>
                        <Icon className={`w-6 h-6 ${colorClasses.text}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{feature.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Toggle Switch */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <span className="text-sm font-medium text-gray-700">
                      Feature Status
                    </span>
                    <button
                      onClick={() => handleToggleFeature(feature.id)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        feature.enabled ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          feature.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Status Badge */}
                  <div className="mt-3">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        feature.enabled
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {feature.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Feature Management Notes</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• All users have access to all enabled features by default</li>
            <li>• Disabling a feature will hide it from all user interfaces</li>
            <li>• Existing data for disabled features is preserved</li>
            <li>• Changes take effect immediately after saving</li>
            <li>• "Overseas Education" is the primary feature and cannot be disabled</li>
          </ul>
        </div>

        {/* Confirmation Modal */}
        <ConfirmModal
          isOpen={showSaveModal}
          title="Save Feature Settings?"
          message="Are you sure you want to save these feature settings? Changes will take effect immediately for all users."
          confirmText="Save Changes"
          cancelText="Cancel"
          onConfirm={handleSave}
          onCancel={() => setShowSaveModal(false)}
        />
      </div>
    </AdminLayout>
  );
};

export default FeatureManagement;
